import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, KeyRound, Loader2, X } from 'lucide-react';

import { CustomHeadersEditor, type CustomHeaderEntry } from '../../../components/CustomHeadersEditor';
import {
  getProviderTypeMeta,
  PROVIDER_MAX_TOKENS_CEILING,
  PROVIDER_MAX_TOKENS_DEFAULTS,
  type ModelProviderResponse,
  type ProviderHeaderInput,
  type ProviderType,
  type UpdateModelProviderRequest,
} from '../api';

interface EditProviderModalProps {
  provider: ModelProviderResponse | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (request: UpdateModelProviderRequest) => void;
}

interface FormState {
  alias: string;
  endpoint: string;
  modelName: string;
  completionDeployment: string;
  embeddingDeployment: string;
  isActive: boolean;
  newApiKey: string;
  maxExtraction: string;
  maxDiscovery: string;
  headers: CustomHeaderEntry[];
}

function buildInitialState(provider: ModelProviderResponse): FormState {
  return {
    alias: provider.alias,
    endpoint: provider.endpoint ?? '',
    modelName: provider.modelName ?? '',
    completionDeployment: provider.completionDeployment ?? '',
    embeddingDeployment: provider.embeddingDeployment ?? '',
    isActive: provider.isActive,
    newApiKey: '',
    maxExtraction:
      provider.maxTokensForExtraction == null ? '' : String(provider.maxTokensForExtraction),
    maxDiscovery:
      provider.maxTokensForDiscovery == null ? '' : String(provider.maxTokensForDiscovery),
    headers: provider.customHeaders.map((h) => ({ name: h.name, value: h.value, isSecret: h.isSecret })),
  };
}

/**
 * Edición segura de un provider. Pre-carga los valores actuales (excepto la API key,
 * que NUNCA se devuelve desde el backend) y envía un body con solo los campos que
 * el usuario haya modificado.
 *
 * Seguridad para la API key:
 *   - El campo arranca vacío y nunca refleja el secret almacenado.
 *   - `type=password` + `autoComplete="new-password"` para que ni el navegador ni un
 *     gestor de contraseñas autorellenen con un valor cached.
 *   - Aviso visual en cuanto el campo tiene contenido: una rotación en Key Vault es
 *     una acción que el operador debe ver y confirmar conscientemente.
 *   - Si el campo queda vacío, no se incluye en el request — el secret existente
 *     en Key Vault permanece intacto.
 *
 * Sentinel para los caps de tokens: si el provider tenía un override y el operador
 * vacía el input, se envía `0` (el backend lo interpreta como "limpiar override y
 * volver al default"). Si nunca hubo override y el operador lo deja vacío, el
 * campo se omite y no llega cambio.
 */
export function EditProviderModal({
  provider,
  busy,
  onClose,
  onSubmit,
}: EditProviderModalProps): JSX.Element | null {
  const [state, setState] = useState<FormState | null>(null);

  // Re-bootstrap el form cuando cambia el provider que se está editando (o se cierra
  // el modal). Limpia también el campo de API key para que un segundo open no
  // arrastre lo que el operador tecleó en una sesión anterior.
  useEffect(() => {
    if (provider === null) {
      setState(null);
      return;
    }
    setState(buildInitialState(provider));
  }, [provider]);

  useEffect(() => {
    if (provider === null) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !busy) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [provider, busy, onClose]);

  // Hooks must run on every render, in the same order, regardless of whether the modal is open.
  // Computing this above the early-return keeps the hook count stable when `provider` flips from
  // null to a value (otherwise React throws "Rendered more hooks than during the previous render"
  // — the symptom seen when opening the modal for a relay-Ollama provider).
  const storedSecretRefs = useMemo(() => {
    const refs = new Set<string>();
    if (provider !== null) {
      for (const h of provider.customHeaders) {
        if (h.isSecret) refs.add(h.value);
      }
    }
    return refs;
  }, [provider]);

  if (provider === null || state === null) {
    return null;
  }

  const providerType = provider.type as ProviderType;
  const meta = getProviderTypeMeta(providerType);
  const rotatingKey = meta.requiresApiKey && state.newApiKey.length > 0;
  const modelNameCopy = labelForModelName(providerType);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const request = buildUpdateRequest(provider, state);
    onSubmit(request);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-provider-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onClose();
        }
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-xl rounded-2xl border border-slate-200/70 bg-white p-4 shadow-xl sm:p-6"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="edit-provider-title" className="text-lg font-semibold text-slate-900">
              Editar provider
            </h2>
            <p className="mt-1 truncate text-sm text-slate-500">
              {provider.alias} · <span className="font-mono">{provider.providerId}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-full p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" aria-hidden strokeWidth={2} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="label" htmlFor="edit-alias">Alias</label>
            <input
              id="edit-alias"
              className="input"
              value={state.alias}
              onChange={(e) => setState({ ...state, alias: e.target.value })}
              disabled={busy}
            />
          </div>

          {meta.requiresEndpoint ? (
            <div>
              <label className="label" htmlFor="edit-endpoint">{providerType === 'OllamaViaAzureRelay' ? 'Relay URI' : 'Endpoint'}</label>
              <input
                id="edit-endpoint"
                className="input"
                placeholder={
                  providerType === 'OllamaViaAzureRelay'
                    ? 'relay+wss://relay.servicebus.windows.net/ollama?keyName=sender'
                    : 'https://api.example.com'
                }
                value={state.endpoint}
                onChange={(e) => setState({ ...state, endpoint: e.target.value })}
                disabled={busy}
              />
              {providerType === 'OllamaViaAzureRelay' ? (
                <p className="mt-1 text-xs text-slate-500">
                  Azure Service Bus Relay (Hybrid Connection). La SAS key se rota vía el campo &quot;Nueva API key&quot; arriba.
                </p>
              ) : null}
            </div>
          ) : null}

          <div>
            <label className="label" htmlFor="edit-model">{modelNameCopy.label}</label>
            <input
              id="edit-model"
              className="input"
              placeholder={modelNameCopy.placeholder}
              value={state.modelName}
              onChange={(e) => setState({ ...state, modelName: e.target.value })}
              disabled={busy}
            />
            {modelNameCopy.help !== null ? (
              <p className="mt-1 text-xs text-slate-500">{modelNameCopy.help}</p>
            ) : null}
          </div>

          {providerType === 'ContentUnderstanding' ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="edit-cu-completion">Modelo completion (deployment)</label>
                <input
                  id="edit-cu-completion"
                  className="input"
                  placeholder="gpt-4.1"
                  value={state.completionDeployment}
                  onChange={(e) => setState({ ...state, completionDeployment: e.target.value })}
                  disabled={busy}
                />
              </div>
              <div>
                <label className="label" htmlFor="edit-cu-embedding">Modelo embedding (deployment)</label>
                <input
                  id="edit-cu-embedding"
                  className="input"
                  placeholder="text-embedding-3-large"
                  value={state.embeddingDeployment}
                  onChange={(e) => setState({ ...state, embeddingDeployment: e.target.value })}
                  disabled={busy}
                />
              </div>
              <p className="text-xs text-slate-500 sm:col-span-2">
                Nombres de los <em>deployments</em> en tu recurso Foundry. El modelo subyacente debe ser uno
                soportado por Content Understanding: <code className="font-mono">gpt-5.2</code> (recomendado),
                <code className="font-mono"> gpt-4.1</code> o <code className="font-mono">gpt-4.1-mini</code>
                {' '}(<code className="font-mono">gpt-5.4</code> no es compatible con CU). Vacío usa
                {' '}<code className="font-mono">gpt-4.1</code> / <code className="font-mono">text-embedding-3-large</code>.
              </p>
            </div>
          ) : null}

          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200/70 bg-slate-50/60 p-3">
            <input
              type="checkbox"
              checked={state.isActive}
              onChange={(e) => setState({ ...state, isActive: e.target.checked })}
              disabled={busy}
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-slate-700">
              <span className="font-medium text-slate-900">Activo</span>
              <span className="ml-2 text-xs text-slate-500">
                Si lo desactivas, los tipos de documento que lo referencien no podrán procesar nuevos jobs.
              </span>
            </span>
          </label>

          {meta.requiresApiKey ? (
            <ApiKeyField
              value={state.newApiKey}
              busy={busy}
              rotating={rotatingKey}
              onChange={(value) => setState({ ...state, newApiKey: value })}
            />
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MaxTokensField
              id="edit-max-extraction"
              label="Tokens máx. extracción"
              defaultValue={PROVIDER_MAX_TOKENS_DEFAULTS.extraction}
              original={provider.maxTokensForExtraction ?? null}
              value={state.maxExtraction}
              busy={busy}
              onChange={(value) => setState({ ...state, maxExtraction: value })}
            />
            <MaxTokensField
              id="edit-max-discovery"
              label="Tokens máx. discovery"
              defaultValue={PROVIDER_MAX_TOKENS_DEFAULTS.discovery}
              original={provider.maxTokensForDiscovery ?? null}
              value={state.maxDiscovery}
              busy={busy}
              onChange={(value) => setState({ ...state, maxDiscovery: value })}
            />
          </div>

          <CustomHeadersEditor
            value={state.headers}
            busy={busy}
            onChange={(headers) => setState({ ...state, headers })}
            storedSecretRefs={storedSecretRefs}
          />
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden strokeWidth={2} /> : null}
            {busy ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </div>
  );
}

interface ApiKeyFieldProps {
  value: string;
  busy: boolean;
  rotating: boolean;
  onChange: (value: string) => void;
}

function ApiKeyField({ value, busy, rotating, onChange }: ApiKeyFieldProps): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200/70 bg-slate-50/60 p-3">
      <label className="label flex items-center gap-2" htmlFor="edit-api-key">
        <KeyRound className="h-3.5 w-3.5 text-slate-500" aria-hidden strokeWidth={2.25} />
        Nueva API key
        <span className="ml-1 text-xs font-normal normal-case text-slate-500">
          (dejar vacío para mantener la actual)
        </span>
      </label>
      <input
        id="edit-api-key"
        type="password"
        autoComplete="new-password"
        spellCheck={false}
        placeholder="••••••••••"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
        className="input"
      />
      {rotating ? (
        <p className="mt-2 inline-flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800 ring-1 ring-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden strokeWidth={2.25} />
          <span>
            Se rotará el secret en Key Vault. La key anterior dejará de funcionar al guardar.
          </span>
        </p>
      ) : (
        <p className="mt-1 text-xs text-slate-500">
          La key actual nunca se muestra ni se descarga al navegador.
        </p>
      )}
    </div>
  );
}

interface MaxTokensFieldProps {
  id: string;
  label: string;
  defaultValue: number;
  original: number | null;
  value: string;
  busy: boolean;
  onChange: (value: string) => void;
}

function MaxTokensField({
  id,
  label,
  defaultValue,
  original,
  value,
  busy,
  onChange,
}: MaxTokensFieldProps): JSX.Element {
  const willClear = original != null && value.trim() === '';
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
        <span className="ml-2 text-xs font-normal normal-case text-slate-500">
          Vacío = {defaultValue}
        </span>
      </label>
      <input
        id={id}
        type="number"
        min={1}
        max={PROVIDER_MAX_TOKENS_CEILING}
        className="input"
        placeholder={String(defaultValue)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
      />
      {willClear ? (
        <p className="mt-1 text-xs text-slate-500">
          Se eliminará el override y volverá al default ({defaultValue}).
        </p>
      ) : null}
    </div>
  );
}

/**
 * Copy del campo ModelName por tipo de provider. La mayoría de tipos comparten
 * el label genérico "Modelo / deployment" sin placeholder ni texto de ayuda;
 * Content Understanding necesita un copy distinto porque el valor no es un nombre
 * de modelo LLM sino un base analyzer opcional (p. ej. `prebuilt-document`).
 */
function labelForModelName(
  providerType: ProviderType,
): { label: string; placeholder: string; help: string | null } {
  if (providerType === 'ContentUnderstanding') {
    return {
      label: 'Base analyzer (opcional)',
      placeholder: 'prebuilt-document',
      help: null,
    };
  }
  return { label: 'Modelo / deployment', placeholder: '', help: null };
}

/**
 * Diff form state → request body. Solo se incluyen campos que han cambiado.
 *
 * Reglas:
 *   - Strings: trim y comparar. Si el actual queda vacío para un campo opcional
 *     (endpoint/modelName), enviar null para limpiarlo no aplica aquí — el backend
 *     no soporta nullification de esos campos hoy, así que se omiten.
 *   - API key: cualquier valor no-vacío se envía y rota el secret. Vacío = omitir.
 *   - MaxTokens: si pasa de tener override a vacío → enviar 0 (clear sentinel).
 *     Si cambia el número → enviar el nuevo. Si no cambia → omitir.
 */
function buildUpdateRequest(
  provider: ModelProviderResponse,
  state: FormState,
): UpdateModelProviderRequest {
  const request: UpdateModelProviderRequest = { providerId: provider.providerId };

  const aliasTrimmed = state.alias.trim();
  if (aliasTrimmed !== '' && aliasTrimmed !== provider.alias) {
    request.alias = aliasTrimmed;
  }

  const endpointTrimmed = state.endpoint.trim();
  if (endpointTrimmed !== '' && endpointTrimmed !== (provider.endpoint ?? '')) {
    request.endpoint = endpointTrimmed;
  }

  const modelTrimmed = state.modelName.trim();
  if (modelTrimmed !== '' && modelTrimmed !== (provider.modelName ?? '')) {
    request.modelName = modelTrimmed;
  }

  const completionTrimmed = state.completionDeployment.trim();
  if (completionTrimmed !== '' && completionTrimmed !== (provider.completionDeployment ?? '')) {
    request.completionDeployment = completionTrimmed;
  }

  const embeddingTrimmed = state.embeddingDeployment.trim();
  if (embeddingTrimmed !== '' && embeddingTrimmed !== (provider.embeddingDeployment ?? '')) {
    request.embeddingDeployment = embeddingTrimmed;
  }

  if (state.isActive !== provider.isActive) {
    request.isActive = state.isActive;
  }

  if (state.newApiKey !== '') {
    request.newApiKeyPlaintext = state.newApiKey;
  }

  const maxExt = diffMaxTokens(provider.maxTokensForExtraction ?? null, state.maxExtraction);
  if (maxExt !== undefined) {
    request.maxTokensForExtraction = maxExt;
  }

  const maxDisc = diffMaxTokens(provider.maxTokensForDiscovery ?? null, state.maxDiscovery);
  if (maxDisc !== undefined) {
    request.maxTokensForDiscovery = maxDisc;
  }

  if (headersChanged(provider.customHeaders, state.headers)) {
    request.customHeaders = state.headers
      .filter((h) => h.name.trim() !== '')
      .map<ProviderHeaderInput>((h) => ({ name: h.name.trim(), value: h.value, isSecret: h.isSecret }));
  }

  return request;
}

function headersChanged(
  original: ReadonlyArray<{ name: string; value: string; isSecret: boolean }>,
  current: CustomHeaderEntry[],
): boolean {
  if (original.length !== current.length) return true;
  for (let i = 0; i < original.length; i++) {
    const a = original[i] as { name: string; value: string; isSecret: boolean };
    const b = current[i] as CustomHeaderEntry;
    if (a.name !== b.name || a.isSecret !== b.isSecret || a.value !== b.value) return true;
  }
  return false;
}

function diffMaxTokens(original: number | null, currentRaw: string): number | undefined {
  const trimmed = currentRaw.trim();
  if (trimmed === '') {
    // Limpiar override solo cuando antes había uno; si no, omitir (no hay cambio).
    return original == null ? undefined : 0;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  if (original != null && parsed === original) {
    return undefined;
  }
  return parsed;
}
